import { useCallback, useEffect, useState, useRef } from 'react';
import { useClient } from '../../../../context/client';
import { FILTERS } from './useSearchFilters';

/**
 * Provides vehicle filter data from the fitment table with proper filtering
 * of makes based on year and models based on year and make
 */
function useVehicleFilters(filters, setFilter) {
    const [years, setYears] = useState([]);
    const [allMakes, setAllMakes] = useState([]);
    const [allModels, setAllModels] = useState([]);
    const [filteredMakes, setFilteredMakes] = useState([]);
    const [filteredModels, setFilteredModels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [yearLoading, setYearLoading] = useState(false);
    const [makeLoading, setMakeLoading] = useState(false);
    const [modelLoading, setModelLoading] = useState(false);
    const client = useClient();
    const isInitialized = useRef(false);

    // Fallback static data in case API isn't available
    const staticYears = Array.from({ length: 22 }, (_, i) => String(2023 - i));
    const staticMakes = ["Acura", "Alfa Romeo", "Audi", "BMW", "Buick", "Cadillac", "Chevrolet", "Chrysler", "Dodge", "Fiat", "Ford", "Genesis", "GMC", "Honda", "Hyundai", "Infiniti", "Jaguar", "Jeep", "Kia", "Land Rover", "Lexus", "Lincoln", "Mazda", "Mercedes-Benz", "Mini", "Mitsubishi", "Nissan", "Porsche", "Ram", "Subaru", "Tesla", "Toyota", "Volkswagen", "Volvo"];
    const staticModels = ["Accord", "Civic", "CR-V", "Odyssey", "Pilot", "Camry", "Corolla", "RAV4", "Highlander", "Tacoma", "F-150", "Explorer", "Escape", "Mustang", "Edge"];

    // Load all years, makes, and models initially
    useEffect(() => {
        if (isInitialized.current) return;
        
        isInitialized.current = true;
        setLoading(true);
        setYearLoading(true);
        
        // Try to get data from the API, but use static data if it fails
        try {
            client.getVehicleData()
                .then(data => {
                    if (data.years?.length) setYears(data.years);
                    else setYears(staticYears);
                    
                    if (data.makes?.length) setAllMakes(data.makes);
                    else setAllMakes(staticMakes);
                    
                    if (data.models?.length) setAllModels(data.models);
                    else setAllModels(staticModels);
                    
                    // Initialize filtered lists with all options
                    setFilteredMakes(data.makes || staticMakes);
                    setFilteredModels(data.models || staticModels);
                })
                .catch(error => {
                    console.error('Error fetching vehicle data:', error);
                    setYears(staticYears);
                    setAllMakes(staticMakes);
                    setAllModels(staticModels);
                    setFilteredMakes(staticMakes);
                    setFilteredModels(staticModels);
                })
                .finally(() => {
                    setLoading(false);
                    setYearLoading(false);
                });
        } catch (error) {
            console.error('Error in vehicle data API call:', error);
            setYears(staticYears);
            setAllMakes(staticMakes);
            setAllModels(staticModels);
            setFilteredMakes(staticMakes);
            setFilteredModels(staticModels);
            setLoading(false);
            setYearLoading(false);
        }
    }, [client]);

    // When year changes, filter makes based on the selected year
    useEffect(() => {
        const selectedYear = filters[FILTERS.YEAR];
                
        if (!selectedYear) {
            // If no year selected, show all makes
            setFilteredMakes(allMakes);
            return;
        }
        
        setMakeLoading(true);
        
        try {
            // Try to get filtered makes from the API
            client.getFilteredMakes(selectedYear)
                .then(data => {
                    if (data?.makes?.length) {
                        setFilteredMakes(data.makes);
                    } else {
                        // Fallback to using all makes if API fails
                        setFilteredMakes(allMakes);
                    }
                    
                    // Add a small delay before hiding the loading state
                    setTimeout(() => {
                        setMakeLoading(false);
                    }, 300);
                })
                .catch(error => {
                    console.error('Error fetching makes for year:', error);
                    // Fallback to showing all makes
                    setFilteredMakes(allMakes);
                    setMakeLoading(false);
                });
        } catch (error) {
            console.error('Error in filtered makes API call:', error);
            setFilteredMakes(allMakes);
            setMakeLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters[FILTERS.YEAR]]);

    // When make changes (and year is selected), filter models based on year and make
    useEffect(() => {
        const selectedYear = filters[FILTERS.YEAR];
        const selectedMake = filters[FILTERS.MAKE];
                
        if (!selectedYear || !selectedMake) {
            // If either year or make is not selected, don't filter models
            if (!selectedYear) setFilteredModels(allModels);
            return;
        }
        
        setModelLoading(true);
        
        try {
            // Try to get filtered models from the API
            client.getFilteredModels(selectedYear, selectedMake)
                .then(data => {
                    if (data?.models?.length) {
                        setFilteredModels(data.models);
                    } else {
                        // Fallback to using all models if API fails
                        setFilteredModels(allModels);
                    }
                    
                    // Add a small delay before hiding the loading state
                    setTimeout(() => {
                        setModelLoading(false);
                    }, 300);
                })
                .catch(error => {
                    console.error('Error fetching models for year/make:', error);
                    // Fallback to showing all models
                    setFilteredModels(allModels);
                    setModelLoading(false);
                });
        } catch (error) {
            console.error('Error in filtered models API call:', error);
            setFilteredModels(allModels);
            setModelLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters[FILTERS.YEAR], filters[FILTERS.MAKE]]);

    const clearVehicleFilters = useCallback(() => {
        setFilter(FILTERS.YEAR, '');
        setFilter(FILTERS.MAKE, '');
        setFilter(FILTERS.MODEL, '');
        // Reset filtered lists to show all options
        setFilteredMakes(allMakes);
        setFilteredModels(allModels);
    }, [setFilter, allMakes, allModels]);

    return {
        years,
        makes: filteredMakes,
        models: filteredModels,
        loading: loading || yearLoading || makeLoading || modelLoading,
        yearLoading,
        makeLoading,
        modelLoading,
        clearVehicleFilters
    };
}

export default useVehicleFilters; 